import { useEffect, useState } from "react";
import { Users, Gift, UserCheck, Link2 } from "lucide-react";
import api from "@/lib/api";
import { CopyButton, Loader, fmt, StatusBadge, fmtDate, EmptyState } from "@/components/shared";
import { ReferralLeaderboard } from "./ReferralLeaderboard";

export default function Referrals() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/referrals").then((r) => setData(r.data)).catch(() => setData(false)); }, []);
  if (data === null) return <Loader />;

  const referralLink = `${window.location.origin}/signup?ref=${data.referral_code}`;
  const stats = [
    { icon: Users, label: "Total Referrals", value: data.total_referrals, accent: "text-cyan-300 bg-cyan-500/15" },
    { icon: UserCheck, label: "Active Referrals", value: data.active_referrals, accent: "text-emerald-300 bg-emerald-500/15" },
    { icon: Gift, label: "Referral Rewards", value: `${fmt(data.referral_rewards)} USDT`, accent: "text-fuchsia-300 bg-fuchsia-500/15" },
  ];

  return (
    <div className="space-y-6" data-testid="referrals-page">
      <div>
        <p className="label-eyebrow text-xs font-mono uppercase tracking-[0.2em] text-cyan-400">Referrals</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mt-1">Referral Program</h1>
        <p className="text-sm text-slate-400 mt-2">Earn {data.referral_percentage}% when a referred user's first deposit is approved. One-level direct referrals only.</p>
      </div>

      <div className="glass rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4"><Link2 className="w-5 h-5 text-cyan-400" /><h3 className="font-heading text-white">Your Referral Link</h3></div>
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          <code className="flex-1 w-full font-mono text-sm text-cyan-300 bg-slate-900/60 rounded-lg px-4 py-3 break-all border border-cyan-500/10" data-testid="referral-link">{referralLink}</code>
          <CopyButton value={referralLink} testId="referral-link-copy" label="Copy Link" />
        </div>
        <div className="flex items-center gap-3 mt-4">
          <span className="text-sm text-slate-400">Referral Code:</span>
          <code className="font-mono text-cyan-300 bg-cyan-500/10 px-3 py-1 rounded-lg border border-cyan-500/20" data-testid="referral-code">{data.referral_code}</code>
          <CopyButton value={data.referral_code} testId="referral-code-copy" />
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        {stats.map((s) => (
          <div key={s.label} className="glass glass-hover rounded-2xl p-5">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${s.accent}`}><s.icon className="w-5 h-5" /></div>
            <p className="font-mono text-2xl font-bold text-white">{s.value}</p>
            <p className="text-xs text-slate-500 mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="glass rounded-2xl p-6">
          <h3 className="font-heading text-lg text-white mb-5">Direct Referrals</h3>
          {data.history?.length ? (
            <div className="space-y-3">
              {data.history.map((r) => (
                <div key={r.id} className="flex items-center justify-between bg-slate-900/40 rounded-xl p-3.5 border border-white/5">
                  <div><p className="text-sm text-slate-200">{r.referred_name}</p><p className="text-xs text-slate-500">{fmtDate(r.created_at)}</p></div>
                  <StatusBadge status={r.status} />
                </div>
              ))}
            </div>
          ) : <EmptyState icon={Users} title="No referrals yet" subtitle="Share your link to start earning." />}
        </div>
        <div className="glass rounded-2xl p-6">
          <h3 className="font-heading text-lg text-white mb-5">Reward History</h3>
          {data.rewards?.length ? (
            <div className="space-y-3">
              {data.rewards.map((r) => (
                <div key={r.id} className="flex items-center justify-between bg-slate-900/40 rounded-xl p-3.5 border border-white/5">
                  <div><p className="font-mono text-sm text-emerald-300">+{fmt(r.reward_amount)} USDT</p><p className="text-xs text-slate-500">{r.percentage}% · {fmtDate(r.created_at)}</p></div>
                  <StatusBadge status={r.status} />
                </div>
              ))}
            </div>
          ) : <EmptyState icon={Gift} title="No rewards yet" subtitle="Rewards appear after a referral's first approved deposit." />}
        </div>
      </div>

      <ReferralLeaderboard />
    </div>
  );
}
